import React, { FunctionComponent, ReactElement, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { DefaultTheme, useTheme } from 'styled-components/native'

import { ChatBubbleText } from './ChatBubbleText'
import { tID } from '../../utils/utils'

export type ChatBubbleProps = {
  body: ReactElement<View | typeof ChatBubbleText>
  borderColor?: string
  backgroundColor?: string | string[]
  messageType?: string
  footer?: ReactNode
}

const ChatBubbleContainer = styled(LinearGradient)<{ borderColor: string }>(({ borderColor }) => ({
  borderRadius: '20px',
  flexDirection: 'column',
  borderColor: `${borderColor}`,
  borderWidth: '1px',
  overflow: 'hidden',
}))

const OutOfOffice = styled.View<{ theme: DefaultTheme }>(({ theme }) => {
  const componentColors = theme.colors.components.chatBubble.outOfOffice
  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: componentColors.background,
    padding: theme.spacing['16px'],
    color: componentColors.text,
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
  }
})

/**
 * Text bubble that is shown in the messenger for each message
 */
let ChatBubble: FunctionComponent<ChatBubbleProps> = ({
  body,
  backgroundColor,
  borderColor,
  messageType = '',
  footer,
}) => {
  const { colors } = useTheme()
  const componentColors = colors.components.chatBubble
  const gradientColors =
    typeof backgroundColor === 'string'
      ? ([backgroundColor || componentColors.background, backgroundColor || componentColors.background] as const)
      : ([
          backgroundColor?.[0] || componentColors.background,
          backgroundColor?.[1] || componentColors.background,
        ] as const)

  return (
    <ChatBubbleContainer
      colors={gradientColors}
      start={[0, 0.3]}
      end={[1, 0.7]}
      borderColor={borderColor || componentColors.border}
      testID={tID('ChatBubble')}
    >
      {messageType === 'out_of_office' && (
        <OutOfOffice testID={tID('ChatBubble-outOfOffice')}>
          <FormattedMessage
            defaultMessage='Out of office'
            description='A phrase that says the provider is not working and not available'
          />
        </OutOfOffice>
      )}
      {body}
      {footer}
    </ChatBubbleContainer>
  )
}

ChatBubble = React.memo(ChatBubble)

export { ChatBubble }
